import React, { useEffect, useState } from 'react';
import { useTheme } from '../../ui/src/contextProviders'
import { customStyle } from '@custom/Utils/utils';
import Header from '@custom/Header/Header';
import { ConfirmationPopup, Icon, Loader, Pagination, Search, SuccessModal, Tooltip } from '@ohif/ui';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import getLabStudies from '@custom/rtk-apis/labStudies/getLabStudies';
import { _roleType } from '@custom/Utils/constants/appConstants';
import AssignTelePathologistModal from '@custom/Patients/PatientDetail/component/AssignTelePathologistModal';
import userList from '@custom/rtk-apis/user/userList';
import assignPathologistApi from '@custom/rtk-apis/pathologistuser/assignPathologist';
import { servicesManager } from '../../app/src/App';
import verifyTelePathologyReport from '@custom/rtk-apis/report/verifyPathologyReport';
import studyLabAcceptanceStatus from '@custom/rtk-apis/patients/labAcceptanceStatus'
import { resetLabPatientAcceptance } from '@custom/store/slices/Patients/studyLabAcceptanceStatus';
import getCaseTiming from '@custom/rtk-apis/Setting/getCaseTiming';
import RejectionModal from './RejectionModal';

const LabStudies: React.FC = () => {
  const navigate = useNavigate();
  const { uiNotificationService } = servicesManager.services;
  const role = sessionStorage.getItem('role') || localStorage.getItem('role');
  const hospitalType = sessionStorage.getItem('hospitalType') || localStorage.getItem('hospitalType');
  const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
  const userId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
  // ***** DARK/LIGHT THEME ***** //
  const { isDarkMode } = useTheme();

  // ******* REDUX ******** //
  const dispatch = useDispatch<AppDispatch>();
  const { status, result: labStudies, loading, error } = useSelector(
    (state: RootState) => state.getLabStudiesResponse
  );
  const getCaseTimingResponse = useSelector((state: RootState) => state.getCaseTimingResponse);
  const assignPathologistResponse = useSelector((state: RootState) => state.assignPathologistResponse);
  const verifyTelePathologyResponse = useSelector((state: RootState) => state.verifyTelepathologyReportResponse);
  const studyLabAcceptanceStatusResponse = useSelector((state: RootState) => state.studyLabAcceptanceStatusResponse);

  // ***** STATE ***** //
  const [isLoading, setIsLoading] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [pathologists, setPathologists] = useState([]);
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    message: "",
  })
  const [confirmationModal, setConfirmationModal] = useState({
    title: "",
    message: "",
    type: "",
    isOpen: false,
    onConfirm: null,
  })
  const [rejectionModal, setRejectionModal] = useState({
    isOpen: false,
    item: null,
  });
  const [caseTiming, setCaseTiming] = useState({
  });
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectedPathologists, setRejectedPathologists] = useState([]);
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [search, setSearch] = useState('');

  const fetchData = async (param) => {
    setIsLoading(true);

    const basePayload = (hospitalType === "teraradiology" && role === _roleType.pathologist)
      ? { from: "telepathology" }
      : {};

    await dispatch(getLabStudies({ ...basePayload, ...param }));

    setIsLoading(false);
  };


  // ***** USEEFFECT ***** //
  useEffect(() => {
    const params ={
      search,
      pageNumber: pgNumber,
      pageLimit: pgLimit
    }
    fetchData(params);
  }, [assignPathologistResponse, verifyTelePathologyResponse, studyLabAcceptanceStatusResponse]);

  const calculateAge = (dob: string): string => {
    if (!dob || dob === "--") return "--";
    const birthDate = new Date(dob);
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    if (months < 0) {
      years -= 1;
      months += 12;
    }
    return `${years} yrs, ${months} mon`;
  };


  const tableData = (labStudies?.pageData || []).flatMap((patient: Patient) =>
    (patient.patientMicroscopyLink || []).map((link) => ({
      patient_id: patient?._id,
      patientId: patient.patientId || '--',
      patientName: `${patient.firstName} ${patient.middleName} ${patient.lastName}`.trim() || '--',
      hospitalName: patient?.hospitalName || '--',
      gender: patient.gender || '--',
      age: calculateAge(patient.dob),
      uhid: patient.UHID || '--',
      contact: patient.contactInfo?.mobileNo ? `${patient?.contactInfo?.dialCode} ${patient.contactInfo?.mobileNo}` : '--',
      file: link?.url?.[0]?.split('/')?.pop() || '--',
      id: link.id,
      assignedName: link.assignedName || '--',
      assignedToPathologist: link?.assignedToPathologist,
      assignedToSuperAdmin: link?.assignedToSuperAdmin,
      isCancelled: link?.isCancelled,
      isCancelledBy: link?.isCancelledBy,
      isCancelledByName: link?.isCancelledByName,
      isReportSend: link?.reportSend,
      statusDetails: link?.assignedDetails?.find((item) => item.assignedToPathologist === userId)?.statusDetails?.[0] || {},
      autoRejected: link?.assignedDetails?.find((item) => item.assignedToPathologist === userId)?.autoRejected || false,
      priority: patient?.healthCareInfo?.priority || '--',
      assignedDetails: link?.assignedDetails || [],
      assignedTime: link?.assignedDetails?.find((item) => item.assignedToPathologist === userId)?.assignedTime || '--',
      lastPathologiestStatusDetails: link?.assignedDetails?.find(item => link?.assignedToPathologist === item.assignedToPathologist)?.statusDetails?.[0] || {},
      lastPathologiestAutoRejected: link?.assignedDetails?.find(item => link?.assignedToPathologist === item.assignedToPathologist)?.autoRejected || false,
      lastPathologiestAssignedTime: link?.assignedDetails?.find(item => link?.assignedToPathologist === item.assignedToPathologist)?.assignedTime || '--',
      hospitalId: patient?.hospitalId || '--'
    }))
  );


  const getGenderClassname = (index: number) => {
    const classes = [
      'btn btn__secondary btn__outlined btn--rounded w-170',
      'btn btn__primary btn__outlined btn--rounded w-170',
      'btn btn__purple btn__outlined btn--rounded w-170',
      'btn btn__danger btn__outlined btn--rounded w-170',
      'btn btn__warm btn__outlined btn--rounded w-170'
    ];
    return classes[index % classes.length];
  };

  const getProfileClassname = (index: number) => {
    const classes = [
      'profile__initials initial-first',
      'profile__initials initial-second',
      'profile__initials initial-third',
      'profile__initials initial-forth'
    ];
    return classes[index % classes.length];
  }

  const getProfileName = (name: string) => {
    return name.split(" ").map(word => word.charAt(0).toUpperCase()).join("") || '--'
  }

  const capitaliseWords = (words: string) => {
    return words.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ") || '--'
  }

  const getIcoName = (gender: string) => {
    return gender === "Male" ? "maleIcon" : gender === "Female" ? "femaleIcon" : "othersIcon";
  }

  const handleOpenModal = async (user) => {
    setIsLoading(true);
    const payload = {
      pgLimit: "10000",
      pageNumber: "0",
      search: "",
      hospitalId: hospitalId,
      loggedInUserId: userId
    }
    const response = await dispatch(userList(payload)).unwrap();
     await dispatch(getCaseTiming());
      // console.log(responseCasetiming, "responseCasetiming")
      // setCaseTiming(responseCasetiming?.data?.[0] || {});
    const filteredPathologists = (response?.result?.list || [])
      .filter(user => user.role === _roleType.pathologist)
      .map(user => ({
        _id: user._id,
        name: `Dr. ${user.name}`,
        fromTimeAvailability: user.fromTimeAvailability || "",
        toTimeAvailability: user.toTimeAvailability || "",
        availabilityDay: user.availabilityDay || []
      }));
    setPathologists(filteredPathologists);
    const rejectedPathologiests = user?.assignedDetails?.filter((item) => item.statusDetails?.[0].isRejected === true || item.autoRejected === true).map((item) => item.assignedToPathologist);
    setRejectedPathologists(rejectedPathologiests || []);
    setSelectedPatient(user);
    setIsLoading(false);
    setIsAssignModalOpen(true);
  }

  const handleCloseModal = () => {
    setIsAssignModalOpen(false);
    setSelectedPatient(null);
  }

  const handleAssignTelePathology = (selectedDoctors) => {
    const priority = selectedPatient?.priority  || "";
    let timeLimit;
    // Only calculate and set timeLimit if caseTiming is not empty
    if (Object.keys(caseTiming).length > 0) {
      timeLimit = 600000; // default value
      if (priority === "Critical") {
        timeLimit = caseTiming?.rejection_critical_time;
      } else if (priority === "Priority") {
        timeLimit = caseTiming?.rejection_priority_time;
      } else if (priority === "Routine") {
        timeLimit = caseTiming?.rejection_routine_time;
      }
    }

    // Base payload without timeLimit
    const payload = {
      "patientId": selectedPatient?.patient_id || "",
      "wsiId": selectedPatient?.id || "",
      "pathologistId": selectedDoctors[0] || "",
    };

    // Add timeLimit to payload only if it was calculated (i.e., caseTiming is not empty)
    if (timeLimit !== undefined) {
      payload.timeLimit = timeLimit;
    }
    setIsLoading(true);
    dispatch(assignPathologistApi(payload)).unwrap()
      .then((res) => {
        if (res?.status == "SUCCESS") {
          setSuccessModal({
            isOpen: true,
            message: "Sucessfully assign telePathologist."
          })
        }
        else if (res?.status == "ERROR") {
          uiNotificationService.show({
            title: 'Something went wrong.',
            message: res?.msg || "Something went wrong while assign Telepathologist...",
            type: 'error',
            duration: 2000
          });
        }
      })
    setIsLoading(false);
    setIsAssignModalOpen(false);
  }

   const onPageChangeHandler = (pg) => {
    setPgNumber(pg);
   fetchData({ pageNumber: pg ,pageLimit:pgLimit, search: search });
  };

  const handlePageSizeChange = (e) => {
    setPgLimit(e);
   fetchData({limit:e, pageNumber:pgNumber, search: search });
  };
const searchClick = (searchText) => {
    setSearch(searchText);
    setSearchInputValue(searchText);
    setPgNumber(1);
   fetchData({ search: searchText, pageNumber: 1, pageLimit: pgLimit });
  };
  const handleSendReport = async (item) => {
    setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
    setIsLoading(true);

    const payload = {
      wsiId: item?.id || "",
      uhid: item?.uhid || "",
    }

    await dispatch(verifyTelePathologyReport(payload)).unwrap()
      .then((res) => {
        if (res.status === "SUCCESS") {
          setSuccessModal({
            isOpen: true,
            message: "Successfully send report to hospital."
          })
        } else if (res.status === "ERROR") {
          uiNotificationService.show({
            title: 'Something went wrong.',
            message: res?.msg || "Something went wrong while assign telepathology...",
            type: 'error',
            duration: 2000
          });
        }
      });
    setIsLoading(false);
  }

  const handleAcceptReject = (item: any, action: string, rejectionReason?: string) => {
    setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
    setRejectionModal((prev) => ({ ...prev, isOpen: false }));
    setIsLoading(true);
    const payload = {
      "_id": item?.patient_id || "",
      "microscopyId": item?.id || "",
      "isApproved": action === "Accept" ? true : false,
      "isRejected": action === "Reject" ? true : false,
      "approvalReason": "",
      "rejectionReason": action === "Reject" ? rejectionReason || "" : "",
    }
    dispatch(studyLabAcceptanceStatus(payload))

  }

  useEffect(() => {
          if (getCaseTimingResponse.status === "SUCCESS") {
            setCaseTiming(getCaseTimingResponse?.result?.[0]);
          }
          if (getCaseTimingResponse.status === "FAILED") {
            setCaseTiming({});
          }
      }, [getCaseTimingResponse]);

  useEffect(() => {
    if (studyLabAcceptanceStatusResponse.status === "SUCCESS" && !studyLabAcceptanceStatusResponse.loading) {
      setSuccessModal({
        isOpen: true,
        message: "Successfully accepted study."
      })
      setIsLoading(false);
      dispatch(resetLabPatientAcceptance());
    } else if (studyLabAcceptanceStatusResponse.status === "ERROR" || studyLabAcceptanceStatusResponse.status === "FAILED") {
      uiNotificationService.show({
        title: 'Something went wrong.',
        message: studyLabAcceptanceStatusResponse?.msg || "Something went wrong while processing request.",
        type: 'error',
        duration: 2000
      });
      setIsLoading(false);
      dispatch(resetLabPatientAcceptance());
    }
  }, [studyLabAcceptanceStatusResponse.status, dispatch]);
  return (
    <div
      className="page page__full"
      style={isDarkMode ? { background: '#090c29' } : customStyle.mainBackground}
    >
      <Header title="Laboratory Studies" isBack={false} />
      <div className="page__body">
        <div className='tableWrap'>
          <div className='tableWrap__head'>
            <div className='formRow formRow__3 m_0 w_100 formRow--flexEnd'>
              <div className='formCol'>
                  <Search text="Name" onSearchClick={searchClick} value={searchInputValue} setValue={setSearchInputValue}></Search>
                </div>
            </div>
          </div>
          <div className='tableContainer'>
            <div className='tableContainer__wrap'>
              <table className='table'>
                <thead>
                  <th>Patient Name</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>File Name</th>
                  <th>Case Study</th>
                  {(role == _roleType.superAdmin || role == _roleType.pathologistAdmin) && <th>Hospital</th>}
                  {!(hospitalType === 'teraradiology' && role === _roleType.pathologist) && <th>Assigned To</th>}
                  {(role == _roleType.superAdmin || role == _roleType.pathologistAdmin) && <th>Assigned Date & Time</th>}
                  {(role == _roleType.superAdmin || role == _roleType.pathologistAdmin) && <th>Assign Status</th>}
                  <th>Action</th>
                  {(role == _roleType.superAdmin || role == _roleType.pathologistAdmin) && <th>Send</th>}
                </thead>
                <tbody>
                  {tableData?.length > 0 ? (
                    tableData.map((item, index) => (
                      <tr key={item.microscopyId}>
                        <td style={{ gap: '8px', alignItems: 'center', display: 'flex' }}>
                          <div className={getProfileClassname(index)}>
                            {getProfileName(item.patientName)}
                          </div>
                          <span>
                            {capitaliseWords(item.patientName)}
                          </span>
                        </td>
                        <td>{item.age}</td>
                        <td>
                          <div className='cellText'>
                            <span className={getGenderClassname(index)} style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                              <Icon name={getIcoName(item.gender)} className="h-[24px]" />
                              {item.gender}
                            </span>
                          </div>
                        </td>
                        <td> {item.file.length > 20 ? item.file.substring(0, 20) + "..." : item.file}</td>
                        <td style={{
                          color:
                            item?.priority === "Critical" ? '#FF5C5C' :
                              item?.priority === "Priority" ? '#007bff' :
                                item?.priority === "Routine" ? '#28a745' :
                                  'inherit',
                        }}>
                          {item.priority ? item.priority.charAt(0).toUpperCase() + item.priority.slice(1) : '--'}
                        </td>
                        {(role == _roleType.superAdmin || role == _roleType.pathologistAdmin) && <td>{item?.hospitalName}</td>}
                        {!(hospitalType === 'teraradiology' && role === _roleType.pathologist) && (
                          <td>
                            {item.assignedName !== "--" && !((role == _roleType.superAdmin || role == _roleType.pathologistAdmin) && (item.lastPathologiestStatusDetails.isRejected || item.lastPathologiestAutoRejected) && !item.isCancelled)
                              ? (hospitalType === 'hospital'?'Telepathologist': item.assignedName)
                              : ((role == _roleType.superAdmin || role == _roleType.pathologistAdmin) && !item.isCancelled && (item.assignedName === "--" || item.lastPathologiestStatusDetails.isRejected || item.lastPathologiestAutoRejected)) ? (
                                <button className='btn btn__white' onClick={() => handleOpenModal(item)}>
                                  Assign Pathologist
                                </button>
                              ) : '--'
                            }
                          </td>
                        )}
                        {(role == _roleType.superAdmin || role == _roleType.pathologistAdmin) && (
                          <td>
                            {item.lastPathologiestAssignedTime !== '--'
                              ? new Date(item.lastPathologiestAssignedTime).toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: 'numeric',
                                hour12: true
                              })
                              : '--'}
                          </td>
                        )}
                        {
                          (role == _roleType.superAdmin || role == _roleType.pathologistAdmin) && (
                            <td>
                              {
                                item.lastPathologiestAutoRejected ? (
                                  <span className='text-red-500'>Auto Rejected</span>
                                ) : item.lastPathologiestStatusDetails && Object.keys(item.lastPathologiestStatusDetails).length > 0 ? (
                                  item.lastPathologiestStatusDetails.isRejected ? (
                                    <span className='text-red-500'>Rejected</span>
                                  ) : (
                                    <span className='text-green-500'>Accepted</span>
                                  )
                                ) : (
                                  item.assignedToPathologist && item.assignedName !== '--' ? (
                                    <span className='text-yellow-500'>Pending</span>
                                  ) : (
                                    <span>--</span>
                                  )
                                )
                              }
                            </td>
                          )
                        }
                        <td>
                          {item.isCancelled === true && ((role == _roleType.superAdmin || role == _roleType.pathologistAdmin) || (role === _roleType.pathologist && hospitalType === "teraradiology")) ? (
                            <span className='text-red-500'>Cancelled</span>
                          ) : (
                            role === _roleType.pathologist && hospitalType === "teraradiology" ? (
                              Object.keys(item.statusDetails).length > 0 || item.autoRejected === true ? (
                                <Tooltip
                                  content={item.autoRejected ? "Auto rejected by system" : ""}
                                                                >
                                  <button
                                    className={`btn ${item.autoRejected || item.statusDetails.isRejected ? 'text-red-600 border-red-600' : ''}`}

                                    onClick={() => navigate(`/WSI/${item.uhid}/${item.id}`)}
                                    disabled={item.statusDetails.isRejected || item.autoRejected} // Disabled if rejected
                                  >
                                    {item.autoRejected ? 'Auto Rejected' : item.statusDetails.isRejected ? 'Rejected' : 'Click'}
                                  </button>
                                </Tooltip>
                              ) : (
                                <div className="flex">
                                  <button
                                    className="btn"
                                    style={{ color: "#ff4d4f", marginRight: "8px" }}
                                    onClick={() => {
                                      setRejectionModal({
                                        isOpen: true,
                                        item: item,
                                      });
                                      setRejectionReason(""); // Reset reason when opening modal
                                    }}
                                  >
                                    Reject
                                  </button>
                                  <button
                                    className="btn btn__white__on"
                                    onClick={() => {
                                      setConfirmationModal({
                                        title: "Accept Study",
                                        message: "Are you sure you want to accept this study?",
                                        type: "Accept",
                                        isOpen: true,
                                        onConfirm: () => handleAcceptReject(item, "Accept")
                                      });
                                    }}
                                  >
                                    Accept
                                  </button>
                                </div>
                              )
                            ) : (
                                  (
                                    (role !== _roleType.pathologist) ||
                                    (
                                      role === _roleType.pathologist &&
                                      (
                                        (item.assignedToSuperAdmin === undefined && item.isCancelled === undefined) ||
                                        (item.assignedToSuperAdmin && item.isCancelled === true)
                                      )
                                    )
                                  ) && (
                                    <button
                                      className="btn btn__white"
                                      onClick={() => navigate(`/WSI/${item.uhid}/${item.id}`)}
                                    >
                                      Click
                                    </button>
                                  )
                            )
                          )}
                        </td>
                        {((role == _roleType.superAdmin || role == _roleType.pathologistAdmin) && !item?.isCancelled) &&
                          (item?.isReportSend ?
                            <td>
                              <span style={{ color: "#0f8491" }}>Report Sent</span>
                            </td>
                            :
                            <td>
                              <Icon
                              onClick={() => {
                                setConfirmationModal({
                                  title: "Confirm Report Submission ?",
                                  message: "Are you sure you want to send this report?",
                                  type: "sendReport",
                                  isOpen: true,
                                  onConfirm: () => handleSendReport(item)
                                })
                              }}
                              className="h-7 w-7" name={'sendReportIcon'}/>
                            </td>)
                        }
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center' }}>
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
       {tableData?.length > 0 && <Pagination
          onChangePage={onPageChangeHandler}
          onChangePerPage={handlePageSizeChange}
          totalPage={labStudies?.totalCount}
          currentPage={pgNumber}
          perPage={pgLimit}
        />}
      <Loader isLoaderOpen={isLoading} />
      {isAssignModalOpen && selectedPatient && (
        <AssignTelePathologistModal
          isOpen={isAssignModalOpen}
          onClose={handleCloseModal}
          title='Assign Telepathologist'
          subtitle='Telepathologist'
          patientName={selectedPatient?.patientName}
          rejectedPathologists={rejectedPathologists}
          userData={pathologists}
          isLoading={isLoading}
          onAssign={(selectedDoctors) => handleAssignTelePathology(selectedDoctors)}
        />
      )}
      <SuccessModal
        isSuccessModalOpen={successModal?.isOpen}
        title={successModal?.message}
        handleCloseModal={() => {
          setTimeout(() => {
            setSuccessModal({ isOpen: false, message: "" })
          }, 2000);
        }}
      />
      <ConfirmationPopup
        isOpen={confirmationModal?.isOpen}
        title={confirmationModal?.title}
        message={confirmationModal?.message}
        onConfirm={confirmationModal?.onConfirm}
        onCancel={() => setConfirmationModal((prev) => ({ ...prev, isOpen: false }))}
      />
      <RejectionModal
        isOpen={rejectionModal.isOpen}
        title="Reject Study"
        onConfirm={() => {
          if (rejectionReason.trim() === "") {
            uiNotificationService.show({
              title: "Error",
              message: "Please provide a rejection reason.",
              type: "error",
              duration: 2000,
            });
          } else {
            handleAcceptReject(rejectionModal.item, "Reject", rejectionReason);
            setRejectionReason(""); // Reset reason after submission
          }
        }}
        onCancel={() => {
          setRejectionModal({ isOpen: false, item: null });
          setRejectionReason(""); // Reset reason on cancel
        }}
        textField={
          <textarea
            className="w-full p-2 border rounded mb-4"
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter rejection reason..."
          />
        }
      />
    </div>
  )
}

export default LabStudies

interface MicroscopyLink {
  id: string;
  url: string;
  uploadTime: string;
  assignedToPathologist?: string;
  assignedName: string;
  assignedToSuperAdmin: string;
  isCancelled: boolean;
  isCancelledBy: string;
  isCancelledByName: string;
  reportSend: string;
  assignedDetails: any;
  priority: string;
}

interface Patient {
  _id: string;
  patientId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  gender: string;
  dob: string;
  UHID: string;
  contactInfo: { mobileNo: string, dialCode: string };
  patientMicroscopyLink?: MicroscopyLink[];
  assignedName: string;
  hospitalName: string;
  hospitalId ?: string;
}
